import { useState, useMemo, useEffect, useRef, useCallback } from 'react'
import Navbar from '../components/Navbar/Navbar'
import Hero from '../components/Hero/Hero'
import Guarantees from '../components/Guarantees/Guarantees'
import Sidebar from '../components/Sidebar/Sidebar'
import ListingCard from '../components/ListingCard/ListingCard'
import DetailPage from '../components/DetailPage/DetailPage'
import Chatbot from '../components/Chatbot/Chatbot'
import AuthModal from '../components/AuthModal/AuthModal'
import Favorites from '../components/Favorites/Favorites'
import Cart from '../components/Cart/Cart'
import CheckoutModal from '../components/CheckoutModal/CheckoutModal'
import AdminDashboard from '../components/admin/AdminDashboard'
import UserDashboard from '../components/UserDashboard/UserDashboard'
import Footer from '../components/Footer/Footer'
import ContactSection from '../components/ContactSection/ContactSection'
import Services from '../components/Services/Services'
import AdminFormalitesPage from '../components/Services/AdminFormalitesPage'
import { listingsAPI, authAPI, favoritesAPI, ordersAPI, invoicesAPI, messagesAPI, adoptionAPI, deliveryAPI, animalDocsAPI } from '../api'
import './Home.css'

const PER_PAGE = 9

// Normalise un objet listing API → format frontend
// Appelé à deux endroits : chargement initial + refresh après paiement
const normalizeListings = (apiListings) =>
  apiListings.map(l => ({
    // Identifiants
    id:           l._id,
    _id:          l._id,
    // Infos principales
    title:        l.title,
    desc:         l.desc,
    breed:        l.breed,
    category:     l.category,
    price:        l.price,
    city:         l.city,
    region:       l.region || '',
    age:          l.age,
    sex:          l.sex,
    // Santé
    vaccinated:   l.vaccinated,
    microchipped: l.microchipped,
    pedigree:     l.pedigree,
    // Meta
    featured:     l.featured,
    status:       l.status,
    views:        l.views,
    // Date ISO brute — le formatage est fait dans les composants
    createdAt:    l.createdAt,
    // Images — pas de fallback ici, les composants gèrent l'absence d'image
    images:       l.images || [],
    img:          l.images?.[0]?.url || '',
    imgs:         (l.images || []).map(i => i.url),
    // Vendeur — valeurs réelles depuis la base
    seller:       l.seller     || '',
    sellerType:   l.sellerType || 'Pro',
    phone:        l.phone      || '',
    email:        l.email      || '',
    delivery:     l.delivery   || 'Livraison incluse',
  }))

export default function Home() {
  const [page, setPage]                   = useState('home')
  const [detailId, setDetailId]           = useState(null)
  const [adminPage, setAdminPage]         = useState(false)
  const [searchValue, setSearchValue]     = useState('')
  const [wishlist, setWishlist]           = useState([])
  const [cart, setCart]                   = useState([])
  const [cartOpen, setCartOpen]           = useState(false)
  const [checkoutOpen, setCheckoutOpen]   = useState(false)
  const [snackbar, setSnackbar]           = useState({ open: false, msg: '', type: 'info' })
  const [user, setUser]                   = useState({ name: '', email: '', role: '', loggedIn: false, isActive: true })
  const [adminLoggedIn, setAdminLoggedIn] = useState(false)
  const [authOpen, setAuthOpen]           = useState(false)
  const [listings, setListings]           = useState([])
  const [loadingListings, setLoadingListings] = useState(true)
  const [listingsError, setListingsError]     = useState(null)
  const [activeCat, setActiveCat]         = useState('Tous')
  const [sort, setSort]                   = useState('featured')
  const [region, setRegion]               = useState('Toute la France')
  const [maxPrice, setMaxPrice]           = useState('')
  const [filterVacc, setFilterVacc]       = useState(false)
  const [filterMicro, setFilterMicro]     = useState(false)
  const [filterPedi, setFilterPedi]       = useState(false)
  const [filterPro, setFilterPro]         = useState(false)
  const [filterPart, setFilterPart]       = useState(false)
  const [filterDon, setFilterDon]         = useState(false)
  const [currentPage, setCurrentPage]     = useState(1)

  const userBanned = user.loggedIn && user.isActive === false
  const alertBanned = () => {
    window.alert('Votre compte est banni. Vous ne pouvez pas effectuer cette action tant que le bannissement n\'est pas levé.')
  }

  // ── Messagerie : ouvrir directement l'onglet messages depuis "Contacter l'annonceur" ──
  const [dashboardInitialView, setDashboardInitialView] = useState('overview')
  const [autoOpenListing, setAutoOpenListing]           = useState(false)
  const [contactListing, setContactListing]             = useState(null)
  // 'home' | 'transport' - memorise d'ou on vient pour AdminFormalitesPage
  const [formalitesFrom, setFormalitesFrom]             = useState('home')


  // ── Badges de notifications ──────────────────────────────
  // User : nouvelles commandes/factures non lues
  const [userNotifBadge, setUserNotifBadge] = useState(0)
  // Admin : nouvelles commandes depuis la dernière visite du dashboard
  const [adminNotifBadge, setAdminNotifBadge] = useState(0)
  const adminLastOrderCount = useRef(-1)  // -1 = référence pas encore prise
  const pollingRef          = useRef(null)
  const listingsPollingRef  = useRef(null)

  // ── BroadcastChannel : sync inter-onglets immédiate ──────
  useEffect(() => {
    if (!user.loggedIn) return
    let ch
    try {
      ch = new BroadcastChannel('app_sync_event')
      ch.onmessage = () => { pollNotifications() }
    } catch { /* navigateur sans BroadcastChannel */ }
    return () => { try { ch && ch.close() } catch {} }
  }, [user.loggedIn, user.role]) // eslint-disable-line

  // ── Charger les annonces depuis l'API au démarrage ──
  useEffect(() => {
    const fetchListings = async () => {
      try {
        setLoadingListings(true)
        setListingsError(null)
        const data = await listingsAPI.getAll({ limit: 100 })
        const nextListings = normalizeListings(data.listings)
        setListings(nextListings)
        setCart(cart => cart.filter(item => nextListings.some(listing => listing.id === item.id && listing.status !== 'sold')))
      } catch (err) {
        console.error('Erreur chargement annonces:', err)
        setListingsError('Impossible de charger les annonces.')
      } finally {
        setLoadingListings(false)
      }
    }
    fetchListings()
  }, [])

  // ── Restaurer la session depuis le token JWT ──
  useEffect(() => {
    const token = localStorage.getItem('token')
    if (!token) return
    const restoreSession = async () => {
      try {
        const data = await authAPI.getMe()
        const u = data.user
        setUser({
          name:     u.name,
          email:    u.email,
          role:     u.role,
          loggedIn: true,
          isActive: u.isActive !== false,
          phone:    u.phone,
          address:  u.address,
          city:     u.city,
        })
        if (u.role === 'admin') {
          setAdminLoggedIn(true)
          // adminPage volontairement non forcé — l'admin choisit lui-même
          // d'entrer dans le dashboard via le bouton, pas au reload
        }
        if (u.role === 'user') {
          const favData = await favoritesAPI.getMyIds()
          setWishlist(favData.ids || [])
        }
      } catch {
        localStorage.removeItem('token')
      }
    }
    restoreSession()
  }, [])

  // ── Polling notifications (toutes les 30s quand connecté) ──
  const pollNotifications = useCallback(async () => {
    if (!user.loggedIn) return
    try {
      if (user.role === 'user') {
        const [oData, iData, mData, dData, animalDocsData, adoptionData] = await Promise.all([
          ordersAPI.getMy({ limit: 50 }),
          invoicesAPI.getUnreadCount(),
          messagesAPI.getUnreadCount(),
          deliveryAPI.getMyPendingCount(),
          animalDocsAPI.getMyDocs(),
          adoptionAPI.getMy(),
        ])
        const unreadOrders   = (oData.orders || []).filter(o => !o.readByBuyer).length
        const unreadInvoices = iData.count || 0
        const unreadMessages = mData.count || 0
        const unreadDocs     = dData.count || 0
        const unreadAnimalDocs = (animalDocsData.docs || []).filter(d => !d.readByUser).length
        const unreadAdoptionForms = (adoptionData.forms || []).length
        setUserNotifBadge(unreadOrders + unreadInvoices + unreadMessages + unreadDocs + unreadAnimalDocs + unreadAdoptionForms)
      } else if (user.role === 'admin') {
        const [sData, mData, aData, lData] = await Promise.all([
          ordersAPI.getStats(),
          messagesAPI.getUnreadCount(),
          adoptionAPI.getUnreadCountAdmin(),
          listingsAPI.getAllAdmin({ limit: 200, status: 'pending' }),
        ])
        // Nouvelles commandes
        const total = sData.stats?.total ?? 0
        if (adminLastOrderCount.current === -1) {
          adminLastOrderCount.current = total
        } else if (total > adminLastOrderCount.current) {
          setAdminNotifBadge(n => n + (total - adminLastOrderCount.current))
          adminLastOrderCount.current = total
        }
        // Annonces en attente + messages non lus + formulaires adoption non lus
        const pendingListings = (lData.listings || []).filter(l => l.status === 'pending').length
        const extras = pendingListings + (mData.count || 0) + (aData.count || 0)
        setAdminNotifBadge(prev => Math.max(prev, extras + Math.max(0, total - adminLastOrderCount.current)))
      }
    } catch { /* silencieux */ }
  }, [user.loggedIn, user.role])

  useEffect(() => {
    if (!user.loggedIn) {
      setUserNotifBadge(0)
      setAdminNotifBadge(0)
      return
    }
    pollNotifications()
    pollingRef.current = setInterval(pollNotifications, 10000)
    return () => clearInterval(pollingRef.current)
  }, [user.loggedIn, user.role, pollNotifications])

  // ── Polling annonces : rafraîchit silencieusement le catalogue toutes les 60s ──
  useEffect(() => {
    const refreshListings = async () => {
      try {
        const data = await listingsAPI.getAll({ limit: 100 })
        const nextListings = normalizeListings(data.listings)
        setListings(nextListings)
        setCart(cart => cart.filter(item => nextListings.some(listing => listing.id === item.id && listing.status !== 'sold')))
      } catch { /* silencieux */ }
    }
    listingsPollingRef.current = setInterval(refreshListings, 60000)
    return () => clearInterval(listingsPollingRef.current)
  }, [])

  const catCounts = useMemo(() => {
    const counts = {}
    listings.forEach(l => { counts[l.category] = (counts[l.category] || 0) + 1 })
    return counts
  }, [listings])

  const totalListings = listings.length

  const filtered = useMemo(() => {
    const q = searchValue.toLowerCase().trim()
    let list = listings.filter(l => {
      if (l.status !== 'active' && l.status !== 'sold') return false
      if (activeCat !== 'Tous' && l.category !== activeCat) return false
      if (q && !l.title.toLowerCase().includes(q) && !l.breed.toLowerCase().includes(q) && !l.city.toLowerCase().includes(q)) return false
      if (region !== 'Toute la France' && l.region !== region) return false
      if (maxPrice && l.price > parseInt(maxPrice)) return false
      if (filterVacc && !l.vaccinated) return false
      if (filterMicro && !l.microchipped) return false
      if (filterPedi && !l.pedigree) return false
      if (filterPro  && l.sellerType !== 'Pro' && l.sellerType !== 'Eleveur') return false
      if (filterPart && l.sellerType !== 'Particulier' && l.sellerType !== 'Part') return false
      if (filterDon  && l.sellerType !== 'Don') return false
      return true
    })
    if (sort === 'price-asc')  list = [...list].sort((a, b) => a.price - b.price)
    if (sort === 'price-desc') list = [...list].sort((a, b) => b.price - a.price)
    if (sort === 'newest')     list = [...list].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    if (sort === 'featured')   list = [...list].sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0))
    return list
  }, [listings, searchValue, activeCat, sort, region, maxPrice, filterVacc, filterMicro, filterPedi, filterPro, filterPart, filterDon])

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE))
  const paginated  = filtered.slice((currentPage - 1) * PER_PAGE, currentPage * PER_PAGE)

  const goDetail  = (id) => {
    setDetailId(id)
    setPage('detail')
    window.scrollTo(0, 0)
    // Incrémenter les vues localement (le backend les incrémente aussi via getListingById)
    setListings(ls => ls.map(l => (l.id === id || l._id === id) ? { ...l, views: (l.views || 0) + 1 } : l))
  }
  const goHome    = ()   => { setPage('home'); setDetailId(null) }

  const toggleFav = async (id) => {
    if (!user.loggedIn) { setAuthOpen(true); return }
    if (userBanned) { alertBanned(); return }
    try {
      await favoritesAPI.toggle(id)
      setWishlist(w => w.includes(id) ? w.filter(x => x !== id) : [...w, id])
    } catch (err) {
      console.error('Erreur favori:', err)
    }
  }

  const addToCart = (listing) => {
    if (listing.status === 'sold') return
    if (userBanned) { alertBanned(); return }
    setCart(c => {
      const exists = c.find(i => i.id === listing.id)
      if (exists) return c
      return [...c, { ...listing, qty: 1 }]
    })
    setCartOpen(true)
  }

  const removeFromCart = (id) => setCart(c => c.filter(i => i.id !== id))

  // Ouvre le CheckoutModal Stripe — accessible admin ET user (l'admin hérite
  // des fonctionnalités user, dont l'achat)
  const handlePay = () => {
    if (cart.length === 0) return
    if (!user.loggedIn) { setAuthOpen(true); return }
    if (userBanned) { alertBanned(); return }
    setCartOpen(false)
    setCheckoutOpen(true)
  }

  // Appelé par CheckoutModal après confirmation Stripe réussie
  const showSnack = (msg, type = 'info', duration = 8000) => {
    setSnackbar({ open: true, msg, type })
    setTimeout(() => setSnackbar(s => ({ ...s, open: false })), duration)
  }

  const handleCheckoutSuccess = async (data) => {
    setCheckoutOpen(false)
    setCart([])
    setUserNotifBadge(n => n + (data.orders?.length || 1))
    showSnack(
      'Virement enregistré · En attente de confirmation du paiement. Une fois confirmé, téléchargez votre facture dans Mon espace > Mes factures.',
      'wire',
      10000
    )
    try {
      const refreshed = await listingsAPI.getAll({ limit: 100 })
      const nextListings = normalizeListings(refreshed.listings)
      setListings(nextListings)
      setCart(cart => cart.filter(item => nextListings.some(listing => listing.id === item.id && listing.status !== 'sold')))
    } catch(e) {}
  }

  const resetFilters = () => {
    setActiveCat('Tous'); setRegion('Toute la France'); setMaxPrice('')
    setFilterVacc(false); setFilterMicro(false); setFilterPedi(false); setFilterPro(false)
    setFilterPart(false); setFilterDon(false)
    setSort('featured'); setCurrentPage(1); setSearchValue('')
  }

  const goProfile = () => { setPage('profile'); window.scrollTo(0, 0) }

  // Ouvre l'espace utilisateur directement sur l'onglet "Mes messages",
  // utilisé par le bouton "Contacter l'annonceur" depuis une fiche annonce
  const goProfileMessages = (listing = null) => {
    if (userBanned) { alertBanned(); return }
    setContactListing(listing)
    setDashboardInitialView('messages')
    goProfile()
  }


  const goTransport = () => {
    if (!user.loggedIn) { setAuthOpen(true); return }
    if (userBanned) { alertBanned(); return }
    setDashboardInitialView('transport')
    setContactListing(null)
    goProfile()
  }

  const handleUserLogout = () => {
    authAPI.logout()
    setUser({ name: '', email: '', role: '', loggedIn: false })
    setWishlist([])
    setAdminLoggedIn(false)
    setAdminPage(false)
    setPage('home')
    window.scrollTo(0, 0)
  }

  const handleAdminLogout = () => {
    authAPI.logout()
    setUser({ name: '', email: '', role: '', loggedIn: false })
    setAdminLoggedIn(false)
    setAdminPage(false)
  }

  const scrollToCatalog = () => {
    document.getElementById('catalog-top')?.scrollIntoView({ behavior: 'smooth' })
  }

  // ── Composants partagés toujours montés, quelle que soit la page ──
  // (Cart, CheckoutModal, AuthModal) — c'est ce qui garantit que le
  // checkout fonctionne aussi bien depuis le dashboard admin ou user
  // que depuis la page d'accueil.
  const sharedOverlays = (
    <>
      <Cart
        isOpen={cartOpen}
        items={cart}
        onClose={() => setCartOpen(false)}
        onRemove={removeFromCart}
        onDetail={(id) => { setCartOpen(false); setAdminPage(false); setPage('home'); goDetail(id) }}
        isLoggedIn={user.loggedIn}
        onAuthOpen={() => setAuthOpen(true)}
        onPay={handlePay}
      />
      <CheckoutModal
        isOpen={checkoutOpen}
        items={cart}
        onClose={() => setCheckoutOpen(false)}
        onSuccess={handleCheckoutSuccess}
      />
      {snackbar.open && (
        <div className={"home-snackbar home-snackbar-" + snackbar.type}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{flexShrink:0,marginTop:2}}>
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
          </svg>
          <span>{snackbar.msg}</span>
          <button className="home-snackbar-close" onClick={() => setSnackbar(s => ({...s, open: false}))}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 6L6 18M6 6l12 12"/></svg>
          </button>
        </div>
      )}

      <AuthModal
        isOpen={authOpen}
        onClose={() => setAuthOpen(false)}
        onUserLogin={async (data) => {
          setUser({
            name:     data.name || data.email.split('@')[0],
            email:    data.email,
            role:     data.role || 'user',
            loggedIn: true,
            isActive: data.isActive !== false,
            phone:    data.phone,
            address:  data.address,
            city:     data.city,
          })
          setAuthOpen(false)
          if (data.role === 'admin') {
            setAdminLoggedIn(true)
            setAdminPage(true)
          } else {
            try {
              const favData = await favoritesAPI.getMyIds()
              setWishlist(favData.ids || [])
            } catch {}
          }
        }}
        onUserRegister={async (data) => {
          setUser({
            name:     data.name,
            email:    data.email,
            role:     'user',
            loggedIn: true,
            isActive: data.isActive !== false,
            phone:    data.phone,
            address:  data.address,
            city:     data.city,
          })
          setAuthOpen(false)
        }}
      />
    </>
  )

  // Dashboard admin
  if (adminPage && adminLoggedIn) return (
    <>
      <AdminDashboard
        listings={listings}
        setListings={setListings}
        onBack={() => setAdminPage(false)}
        onLogout={handleAdminLogout}
        onCartClick={() => setCartOpen(true)}
        cartCount={cart.length}
        notifBadge={adminNotifBadge}
        autoOpenListing={autoOpenListing}
        onListingFormOpened={() => setAutoOpenListing(false)}
      />
      {sharedOverlays}
    </>
  )

  // Page formalités administratives
  if (page === 'admin-formalites') return (
    <>
      <AdminFormalitesPage
        onBack={() => {
          if (formalitesFrom === 'transport') {
            setFormalitesFrom('home')
            setPage('profile')
            setDashboardInitialView('transport')
          } else {
            setPage('home')
          }
          window.scrollTo(0, 0)
        }}
        user={user}
        onAuthClick={() => setAuthOpen(true)}
      />
      {sharedOverlays}
      <Chatbot />
    </>
  )

  // Dashboard utilisateur
  if (page === 'profile') return (
    <>
      <UserDashboard
        user={user}
        wishlist={wishlist}
        onLogout={handleUserLogout}
        onFav={toggleFav}
        onDetailClick={(id) => { goDetail(id) }}
        onCategoryClick={(cat) => { setActiveCat(cat); setPage('home'); setCurrentPage(1); window.scrollTo(0,0) }}
        onBack={goHome}
        onUpdateUser={(data) => setUser(u => ({ ...u, ...data }))}
        onAdminFormalites={(from) => { setFormalitesFrom(from || 'home'); setPage('admin-formalites'); window.scrollTo(0, 0) }}
        initialView={dashboardInitialView}
        contactListing={contactListing}
        autoOpenListing={autoOpenListing}
        onListingFormOpened={() => setAutoOpenListing(false)}
      />
      {sharedOverlays}
    </>
  )

  return (
    <div>
      <Navbar
        searchValue={searchValue}
        onSearchChange={(v) => {
          setSearchValue(v)
          setCurrentPage(1)
          // Si on n'est pas sur la page d'accueil, y revenir
          if (page !== 'home') { setPage('home'); setDetailId(null) }
        }}
        onSearchSubmit={() => {
          setCurrentPage(1)
          setPage('home')
          setDetailId(null)
          // Petit délai pour laisser le rendu se faire avant le scroll
          setTimeout(() => {
            document.getElementById('catalog-top')?.scrollIntoView({ behavior: 'smooth' })
          }, 80)
        }}
        wishlistCount={wishlist.length}
        cartCount={cart.length}
        user={user}
        notifBadge={user.role === 'admin' ? adminNotifBadge : userNotifBadge}
        onLogoClick={() => { resetFilters(); goHome(); window.scrollTo(0, 0) }}
        onFavoritesClick={() => { setPage('favorites'); window.scrollTo(0, 0) }}
        onCartClick={() => setCartOpen(true)}
        onAuthClick={() => { setAuthOpen(true) }}
        onDashboardClick={() => { setAdminNotifBadge(0); adminLastOrderCount.current = -1; setAdminPage(true); window.scrollTo(0, 0) }}
        onProfileClick={() => { setUserNotifBadge(0); setDashboardInitialView('overview'); setContactListing(null); goProfile() }}
        onServicesClick={() => { document.getElementById('nos-services')?.scrollIntoView({ behavior: 'smooth' }) }}
        onContactClick={() => { document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' }) }}
        onPublishClick={() => {
          if (userBanned) { alertBanned(); return }
          setAutoOpenListing(true)
          if (user.role === 'admin') {
            setAdminPage(true)
          } else {
            setDashboardInitialView('listings')
            setContactListing(null)
            goProfile()
          }
        }}
      />

      {page === 'detail' ? (
        <DetailPage
          listing={listings.find(l => l.id === detailId)}
          allListings={listings}
          wishlist={wishlist}
          onBack={goHome}
          onFav={toggleFav}
          onContact={(item) => { setAdminPage(false); goProfileMessages(item) }}
          onDetailClick={goDetail}
          onAddCart={addToCart}
          isLoggedIn={user.loggedIn}
          onAuthOpen={() => { setAuthOpen(true) }}
          onCategoryClick={(cat) => { setActiveCat(cat); setPage('home'); setCurrentPage(1); window.scrollTo(0,0) }}
        />

      ) : page === 'favorites' ? (
        <Favorites
          listings={listings}
          wishlist={wishlist}
          onFav={toggleFav}
          onDetail={goDetail}
          onBack={goHome}
          onCategoryClick={(cat) => { setActiveCat(cat); setPage('home'); setCurrentPage(1); window.scrollTo(0,0) }}
        />

      ) : (
        <>
          <Hero onViewListings={scrollToCatalog} />
          <Guarantees />

          <div className="catalog-layout" id="catalog-top">
            <Sidebar
              activeCat={activeCat}
              catCounts={catCounts}
              totalListings={totalListings}
              region={region}
              maxPrice={maxPrice}
              filterVacc={filterVacc}
              filterMicro={filterMicro}
              filterPedi={filterPedi}
              filterPro={filterPro}
              filterPart={filterPart}
              filterDon={filterDon}
              onCatChange={(cat) => { setActiveCat(cat); setCurrentPage(1) }}
              onRegionChange={(v) => { setRegion(v); setCurrentPage(1) }}
              onMaxPriceChange={(v) => { setMaxPrice(v); setCurrentPage(1) }}
              onVaccChange={(v) => { setFilterVacc(v); setCurrentPage(1) }}
              onMicroChange={(v) => { setFilterMicro(v); setCurrentPage(1) }}
              onPediChange={(v) => { setFilterPedi(v); setCurrentPage(1) }}
              onProChange={(v) => { setFilterPro(v); setCurrentPage(1) }}
              onPartChange={(v) => { setFilterPart(v); setCurrentPage(1) }}
              onDonChange={(v) => { setFilterDon(v); setCurrentPage(1) }}
            />

            <main className="catalog-main">
              <div className="catalog-bar">
                <div className="catalog-count">
                  <strong>{filtered.length}</strong> annonce{filtered.length !== 1 ? 's' : ''}{activeCat !== 'Tous' ? ` en ${activeCat}` : ''}
                </div>
                <select
                  className="catalog-sort"
                  value={sort}
                  onChange={(e) => { setSort(e.target.value); setCurrentPage(1) }}
                >
                  <option value="featured">À la une</option>
                  <option value="newest">Plus récentes</option>
                  <option value="price-asc">Prix croissant</option>
                  <option value="price-desc">Prix décroissant</option>
                </select>
              </div>

              {loadingListings ? (
                <div className="catalog-loading">
                  <div className="catalog-spinner" />
                  <p>Chargement des annonces…</p>
                </div>
              ) : listingsError ? (
                <div className="catalog-error">
                  <p>{listingsError}</p>
                  <button onClick={() => window.location.reload()}>Réessayer</button>
                </div>
              ) : paginated.length === 0 ? (
                <div className="catalog-empty">
                  <div className="catalog-empty-icon">🔍</div>
                  <div className="catalog-empty-title">Aucune annonce trouvée</div>
                  <div className="catalog-empty-sub">Essayez d'autres critères ou réinitialisez les filtres.</div>
                  <button className="catalog-empty-btn" onClick={resetFilters}>Réinitialiser</button>
                </div>
              ) : (
                <div className="catalog-grid">
                  {paginated.map(listing => (
                    <ListingCard
                      key={listing.id}
                      listing={listing}
                      isFav={wishlist.includes(listing.id)}
                      onFav={() => toggleFav(listing.id)}
                      onDetail={() => goDetail(listing.id)}
                      onAddCart={addToCart}
                    />
                  ))}
                </div>
              )}

              {totalPages > 1 && (
                <div className="catalog-pagination">
                  <button className="pag-btn" disabled={currentPage === 1} onClick={() => { setCurrentPage(p => p - 1); scrollToCatalog() }}>‹</button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                    <button key={p} className={`pag-btn${currentPage === p ? ' active' : ''}`} onClick={() => { setCurrentPage(p); scrollToCatalog() }}>{p}</button>
                  ))}
                  <button className="pag-btn" disabled={currentPage === totalPages} onClick={() => { setCurrentPage(p => p + 1); scrollToCatalog() }}>›</button>
                </div>
              )}
            </main>
          </div>
          
          <div id="nos-services">
            <Services
              onContactClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
              onProfileClick={() => { setDashboardInitialView('invoices'); goProfile() }}
              onTrackingClick={goTransport}
              onAdminFormalites={(from) => { setFormalitesFrom(from || 'home'); setPage('admin-formalites'); window.scrollTo(0, 0) }}
              isLoggedIn={user.loggedIn}
              isActive={user.isActive !== false}
              onAuthClick={() => setAuthOpen(true)}
            />
          </div>

          <ContactSection />

          <Footer
            onCategoryClick={(cat) => { setActiveCat(cat); setPage('home'); setCurrentPage(1); window.scrollTo(0, 0) }}
          />
        </>
      )}

      {sharedOverlays}
      <Chatbot />
    </div>
  )
}